import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel'

/**
 * Carrosséis de exemplos (foto de perfil e stories).
 * No desktop os itens seguem no grid de 3 colunas; abaixo de 768px viram
 * carrossel mostrando um item por vez. O CSS só assume o modo carrossel
 * quando o atributo `data-slider-ready` é aplicado aqui — sem JS, o grid
 * original continua funcionando.
 */

const MOBILE = '(max-width: 767px)'
const AUTOPLAY_PADRAO = 5000

function buildDots(embla: EmblaCarouselType, dotsNode: HTMLElement): void {
  const render = (): void => {
    dotsNode.innerHTML = embla
      .scrollSnapList()
      .map(
        (_, i) =>
          `<button type="button" role="tab" aria-selected="false" aria-label="Exemplo ${i + 1}"></button>`,
      )
      .join('')

    dotsNode.querySelectorAll('button').forEach((dot, i) => {
      dot.addEventListener('click', () => embla.scrollTo(i))
    })
  }

  const sync = (): void => {
    const selected = embla.selectedScrollSnap()
    dotsNode.querySelectorAll('button').forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === selected))
    })
  }

  embla.on('init', render).on('reInit', render)
  embla.on('init', sync).on('reInit', sync).on('select', sync)
}

function setupSlider(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>('[data-slider-viewport]')
  const dots = root.querySelector<HTMLElement>('[data-slider-dots]')
  if (!viewport) return

  const slides = viewport.querySelectorAll('li')
  if (slides.length < 2) return

  const mq = window.matchMedia(MOBILE)
  let embla: EmblaCarouselType | null = null

  const delay = Number(root.dataset.sliderAutoplay ?? AUTOPLAY_PADRAO)
  const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const temAutoplay = delay > 0 && !reduzido

  let timer = 0

  const pausar = (): void => {
    window.clearInterval(timer)
    timer = 0
  }

  const retomar = (): void => {
    if (!temAutoplay || !embla || document.hidden) return
    pausar()
    timer = window.setInterval(() => embla?.scrollNext(), delay)
  }

  const aoTrocarAba = (): void => (document.hidden ? pausar() : retomar())

  const start = (): void => {
    if (embla) return
    root.setAttribute('data-slider-ready', '')
    embla = EmblaCarousel(viewport, { loop: true, align: 'center' })
    if (dots) buildDots(embla, dots)

    if (!temAutoplay) return
    // arrastar ou tocar nas bolinhas reinicia a contagem, para o carrossel não
    // pular logo depois de a pessoa escolher um exemplo
    embla.on('pointerDown', pausar).on('pointerUp', retomar)
    root.addEventListener('click', retomar)
    document.addEventListener('visibilitychange', aoTrocarAba)
    retomar()
  }

  const stop = (): void => {
    if (!embla) return
    pausar()
    document.removeEventListener('visibilitychange', aoTrocarAba)
    root.removeEventListener('click', retomar)
    embla.destroy()
    embla = null
    if (dots) dots.innerHTML = ''
    root.removeAttribute('data-slider-ready')
  }

  const apply = (): void => {
    if (mq.matches) start()
    else stop()
  }

  apply()
  mq.addEventListener('change', apply)
}

function initSliders(): void {
  document
    .querySelectorAll<HTMLElement>('[data-slider]')
    .forEach((root) => setupSlider(root))
}

document.addEventListener('DOMContentLoaded', initSliders)
